import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { UsuarioService } from '../../../../services/shared/usuario.service';
import { User } from '../../../../interfaces/interface-bakend/seguridad/users/user.interface';
import { PerfilesService } from '../../../../services/perfiles/perfiles.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalModificarClaveUsersComponent } from '../modal-modificar-clave/modal-modificar-clave.component';
import { CursosService } from 'src/app/services/cursos/cursos.service';
import { AlumnoTitulo } from 'src/app/interfaces/interface-bakend/cursos/titulo.interface';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-usuarios-listado',
    templateUrl: './usuarios-listado.component.html',
    styleUrls: ['./usuarios-listado.component.scss'],
})
export class UsuariosListadoComponent implements OnInit, AfterViewInit {
    private sortRef: MatSort;
    private paginatorRef: MatPaginator;

    @ViewChild(MatSort) set matSort(ms: MatSort) {
        this.sortRef = ms;
        if (ms && this.dataSource) {
            this.dataSource.sort = ms;
        }
    }
    @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
        this.paginatorRef = mp;
        if (mp && this.dataSource) {
            this.dataSource.paginator = mp;
        }
    }

    displayedColumns: string[] = [
        'nombre',
        'identificacion',
        'perfil',
        'estado',
        'aceptaTerminos',
        'email_verified',
        'pin',
        'created',
        '_id',
    ];

    dataSource = new MatTableDataSource<User>([]);
    flagLoaded = false;
    flagBuscando = false;
    searchTerm = '';
    searchPerformed = false; // << clave: controla si ya se buscó

    /**
     * Filtro propio: normaliza acentos/mayúsculas y matchea por tokens
     * (todas las palabras, en cualquier orden) sólo en campos curados.
     */
    private readonly filtrarUsuario = (data: User, filter: string): boolean => {
        const campos = [
            data.nombrePersona,
            data.apellidoPersona,
            data.email,
            data.identificacion,
            (data.perfil as any)?.descripcion || (data.perfil as any) || '',
        ];
        const haystack = this.normalizar(
            campos.filter((v) => v != null && v !== '').join(' ')
        );
        const tokens = this.normalizar(filter)
            .split(/\s+/)
            .filter(Boolean);
        return tokens.every((token) => haystack.includes(token));
    };

    /** Mapea cada columna del header a su campo real para poder ordenar. */
    private readonly accesoOrden = (
        data: User,
        columna: string
    ): string | number => {
        switch (columna) {
            case 'nombre':
                return `${data.nombrePersona || ''} ${
                    data.apellidoPersona || ''
                }`
                    .trim()
                    .toLowerCase();
            case 'identificacion':
                return data.identificacion || '';
            case 'perfil':
                return (data.perfil as any)?.descripcion || (data.perfil as any) || '';
            case 'estado':
                return data.suspendido ? 'Suspendido' : 'Activo';
            case 'aceptaTerminos':
                return data.aceptaTerminos ? 1 : 0;
            case 'email_verified':
                return data.email_verified ? 1 : 0;
            case 'pin':
                return data.pin || '';
            case 'created':
                return (data as any).createdAt
                    ? new Date((data as any).createdAt).getTime()
                    : 0;
            default:
                return '';
        }
    };

    private normalizar(texto: string): string {
        return (texto || '')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
    }

    constructor(
        private usuariosService: UsuarioService,
        private cursosService: CursosService,
        private router: Router,
        private perfilesService: PerfilesService,
        public dialog: MatDialog,
        private guiMsjService: GuiMsjService,
    ) {}

    ngOnInit(): void {
        this.perfilesService.allowedByUserLogon(this.router.url);
    }

    async ngAfterViewInit() {
        // No cargamos nada al inicio: tabla vacía
        this.vincularTabla();
        this.flagLoaded = true;
    }

    /** Vincula filtro/orden/paginador a la instancia actual de dataSource. */
    private vincularTabla(): void {
        this.dataSource.filterPredicate = this.filtrarUsuario;
        this.dataSource.sortingDataAccessor = this.accesoOrden;
        if (this.sortRef) {
            this.dataSource.sort = this.sortRef;
        }
        if (this.paginatorRef) {
            this.dataSource.paginator = this.paginatorRef;
            this.paginatorRef.firstPage();
        }
    }

    async verUsuario(_id: string) {
        await this.guiMsjService.msjFormSubmit('Espere');
        Swal.showLoading();
        await this.usuariosService.getFindOne(_id);
        const alumno = await this.cursosService.findAlumnoByUsuario(_id);
        await this.cursosService
            .getTitulosAlumnosFindAllByAlumno(alumno._id)
            .then((titulos: AlumnoTitulo[]) => {
                this.cursosService.alumnoTituloData = titulos;
                Swal.close();
            });
        this.router.navigateByUrl('/usuario-modificacion');
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value || '';
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    async searchUsuarios() {
        const term = (this.searchTerm || '').trim();

        this.flagBuscando = true;
        this.searchPerformed = true; // dibuja el área con skeleton
        this.flagLoaded = false;
        try {
            const payload = { searchTerm: term };
            const usuarios =
                await this.usuariosService.getAllUsuariosByTerm(payload);
            this.dataSource = new MatTableDataSource<User>(usuarios || []);
            this.vincularTabla();
        } catch (error) {
            console.error('Error en búsqueda:', error);
            this.dataSource = new MatTableDataSource<User>([]);
            this.dataSource.filterPredicate = this.filtrarUsuario;
            this.vincularTabla();
        } finally {
            this.flagLoaded = true;
            this.flagBuscando = false;
        }
    }

    clearSearch() {
        this.searchTerm = '';
        this.dataSource = new MatTableDataSource<User>([]);
        this.vincularTabla();
        this.searchPerformed = false; // vuelve al estado “aún no buscaste”
    }

    openDialogModificarClave(userId: string): void {
        const user = this.dataSource.data.find((u) => u._id === userId);

        if (user && user.pin) {
            Swal.fire({
                icon: 'warning',
                title: 'Usuario pendiente de activación',
                html: `
                <p>Este usuario todavía no activó su cuenta.</p>
                <p>El <strong>PIN pendiente</strong> indica que aún debe validar su correo electrónico.</p>
                <p>Primero debe ingresar el PIN recibido por email para confirmar que el correo es correcto y activar su cuenta.</p>
                <br>
                <strong>Luego recién podrá modificarse la contraseña.</strong>
            `,
                confirmButtonText: 'Entendido',
            });
            return;
        }
        const dialogRef = this.dialog.open(ModalModificarClaveUsersComponent, {
            width: '620px',
            maxHeight: '90vh',
            disableClose: true,
            panelClass: 'ds-dialog',
            data: userId,
        });

        dialogRef.afterClosed().subscribe(() => {
            if (this.searchPerformed) {
                this.searchUsuarios();
            }
        });
    }
}
